import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { FaCheckCircle, FaChevronLeft, FaChevronRight, FaSyncAlt } from 'react-icons/fa';
import API_BASE_URL from './config/api';
import { torontoDateISO } from './maintenanceOverviewData';
import { getMechanicalCalendarColors, getMechanicalCalendarRange, getMechanicalCalendarVisits, getMechanicalCalendarWeeks, shiftMechanicalCalendarDate } from './mechanicalCalendarData';
import './MaintenanceMechanicalCalendar.css';

const dateObject = iso => new Date(`${iso}T12:00:00Z`);
const formatDate = (iso, options = { month: 'short', day: 'numeric' }) => (
  new Intl.DateTimeFormat('en-CA', { timeZone: 'UTC', ...options }).format(dateObject(iso))
);
const dateRange = visit => visit.startDate === visit.endDate
  ? formatDate(visit.startDate) : `${formatDate(visit.startDate)} – ${formatDate(visit.endDate)}`;

function visitStatus(visit, today) {
  if (['completed', 'complete', 'past'].includes(String(visit.status).trim().toLowerCase())) return 'Completed';
  if (visit.endDate < today) return 'Past booking';
  if (visit.startDate <= today) return 'In progress';
  return 'Scheduled';
}

export default function MaintenanceMechanicalCalendar() {
  const today = torontoDateISO();
  const [anchor, setAnchor] = useState(today);
  const [viewMode, setViewMode] = useState('month');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [refresh, setRefresh] = useState(0);
  const [selected, setSelected] = useState(null);
  const range = useMemo(() => getMechanicalCalendarRange(anchor, viewMode), [anchor, viewMode]);
  const rangeKey = `${range.startDate}:${range.endDate}:${refresh}`;
  const [source, setSource] = useState({ key: '', loading: true, visits: [], error: false });

  useEffect(() => {
    const controller = new AbortController();
    setSource({ key: rangeKey, loading: true, visits: [], error: false });
    setSelected(null);
    (async () => {
      try {
        const token = localStorage.getItem('managerToken');
        const query = new URLSearchParams({ start: range.startDate, end: range.endDate });
        const response = await fetch(`${API_BASE_URL}/api/maintenance/mechanical-calendar?${query}`, {
          signal: controller.signal,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!response.ok) throw new Error('Calendar unavailable');
        const data = await response.json();
        if (data.startDate !== range.startDate || data.endDate !== range.endDate || !Array.isArray(data.mechanicalVisits)) {
          throw new Error('Invalid calendar response');
        }
        if (!controller.signal.aborted) setSource({ key: rangeKey, loading: false, visits: data.mechanicalVisits, error: false });
      } catch (error) {
        if (!controller.signal.aborted) setSource({ key: rangeKey, loading: false, visits: [], error: true });
      }
    })();
    return () => controller.abort();
  }, [range.startDate, range.endDate, rangeKey]);

  const loading = source.key !== rangeKey || source.loading;
  const error = !loading && source.error;
  const visits = useMemo(() => loading || error ? [] : getMechanicalCalendarVisits(source.visits, range), [loading, error, source.visits, range]);
  const weeks = useMemo(() => getMechanicalCalendarWeeks(visits, range), [visits, range]);
  const colors = useMemo(() => getMechanicalCalendarColors(visits), [visits]);
  const periodTitle = viewMode === 'month'
    ? formatDate(anchor, { month: 'long', year: 'numeric' })
    : `${formatDate(range.startDate)} – ${formatDate(range.endDate, { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <main className="mechanical-calendar">
      <header className="mechanical-calendar__header">
        <div><h1>Calendar</h1><p>Mechanical maintenance</p></div>
        <button type="button" className="mechanical-calendar__button" onClick={() => setRefresh(value => value + 1)} disabled={loading}>
          <FaSyncAlt aria-hidden="true" /> Refresh
        </button>
      </header>
      <div className="mechanical-calendar__toolbar">
        <div className="mechanical-calendar__period">
          <button type="button" className="mechanical-calendar__button" aria-label={`Previous ${viewMode}`} onClick={() => setAnchor(value => shiftMechanicalCalendarDate(value, viewMode, -1))}><FaChevronLeft aria-hidden="true" /></button>
          <h2>{periodTitle}</h2>
          <button type="button" className="mechanical-calendar__button" aria-label={`Next ${viewMode}`} onClick={() => setAnchor(value => shiftMechanicalCalendarDate(value, viewMode, 1))}><FaChevronRight aria-hidden="true" /></button>
        </div>
        <div className="mechanical-calendar__view-controls">
          <button type="button" className="mechanical-calendar__button" onClick={() => setAnchor(today)}>Today</button>
          <div className="mechanical-calendar__view-toggle" role="group" aria-label="Calendar view">
            {['month', 'week'].map(mode => <button type="button" key={mode} aria-pressed={viewMode === mode} onClick={() => setViewMode(mode)}>{mode === 'month' ? 'Month' : 'Week'}</button>)}
          </div>
        </div>
      </div>

      {loading ? <p className="mechanical-calendar__message" role="status">Loading mechanical maintenance…</p> : error ? (
        <div className="mechanical-calendar__message mechanical-calendar__message--error" role="alert">
          <p>Mechanical maintenance couldn’t be loaded.</p>
          <button type="button" className="mechanical-calendar__button" onClick={() => setRefresh(value => value + 1)}>Try again</button>
        </div>
      ) : <>
        <p className="mechanical-calendar__summary" role="status">
          {visits.length ? `${visits.length} ${visits.length === 1 ? 'visit' : 'visits'} this ${viewMode}. Multi-day bookings count as one visit.` : `No mechanical maintenance scheduled this ${viewMode}.`}
        </p>
        {isMobile ? <div className="mechanical-calendar__agenda" aria-label={`Mechanical maintenance ${viewMode} agenda`}>
          {visits.map(visit => <button type="button" key={visit.key} className="mechanical-calendar__agenda-visit" style={{ '--booking-color': colors[visit.key] }} onClick={() => setSelected(visit)}>
            <span className="mechanical-calendar__agenda-dates">{dateRange(visit)}</span>
            <strong>{visit.buildingName}</strong>
            <span>{visitStatus(visit, today)}</span>
            <FaChevronRight aria-hidden="true" />
          </button>)}
        </div> : <div className={`mechanical-calendar__calendar mechanical-calendar__calendar--${viewMode}`} aria-label="Mechanical maintenance calendar">
          <div className="mechanical-calendar__weekdays" aria-hidden="true">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day}>{day}</div>)}
          </div>
          <div className="mechanical-calendar__grid" style={{ '--week-count': weeks.length }}>
            {weeks.map((week, index) => (
              <div className="mechanical-calendar__week" key={week.days.find(Boolean) || index}>
                <div className="mechanical-calendar__week-days">
                  {week.days.map((day, column) => day ? (
                    <section key={day} className={`mechanical-calendar__day${day === today ? ' mechanical-calendar__day--today' : ''}${day < today ? ' mechanical-calendar__day--past' : ''}`} aria-label={formatDate(day, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}>
                      <h3><time dateTime={day} aria-current={day === today ? 'date' : undefined}>{viewMode === 'week' ? formatDate(day, { weekday: 'short', month: 'short', day: 'numeric' }) : Number(day.slice(-2))}</time></h3>
                    </section>
                  ) : <div key={`empty-${column}`} className="mechanical-calendar__blank" />)}
                </div>
                <div className="mechanical-calendar__week-bookings">
                  {week.segments.map(({ visit, columnStart, columnSpan, lane, continuesBefore, continuesAfter }) => {
                    const status = visitStatus(visit, today);
                    return <button
                      type="button" key={visit.key}
                      className={`mechanical-calendar__booking${status === 'Completed' ? ' mechanical-calendar__booking--completed' : ''}${continuesBefore ? ' mechanical-calendar__booking--continues-before' : ''}${continuesAfter ? ' mechanical-calendar__booking--continues-after' : ''}`}
                      style={{ gridColumn: `${columnStart} / span ${columnSpan}`, gridRow: lane + 1, '--booking-color': colors[visit.key] }}
                      onClick={() => setSelected(visit)}
                      aria-label={`${visit.buildingName}, ${dateRange(visit)}, ${status}`}
                      title={`${visit.buildingName} · ${dateRange(visit)} · ${status}`}
                    >
                      {continuesBefore && <FaChevronLeft className="mechanical-calendar__continuation" aria-hidden="true" />}
                      {status === 'Completed' && <FaCheckCircle className="mechanical-calendar__completed-icon" aria-hidden="true" />}
                      <span>{visit.buildingName}</span>
                      {continuesAfter && <FaChevronRight className="mechanical-calendar__continuation" aria-hidden="true" />}
                    </button>;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>}
      </>}

      <Modal show={Boolean(selected)} onHide={() => setSelected(null)} centered aria-labelledby="mechanical-calendar-detail-title">
        <Modal.Header closeButton><Modal.Title id="mechanical-calendar-detail-title">{selected?.buildingName}</Modal.Title></Modal.Header>
        <Modal.Body>
          {selected && <div className="mechanical-calendar__details">
            <p>Mechanical maintenance</p>
            <dl><dt>Booked dates</dt><dd>{dateRange(selected)}</dd><dt>Status</dt><dd>{visitStatus(selected, today)}</dd></dl>
            {selected.startDate !== selected.endDate && <p>One maintenance visit across these booked days.</p>}
          </div>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="mechanical-calendar__button" onClick={() => setSelected(null)}>Close</button>
          {selected?.buildingObjectId && <Link className="mechanical-calendar__button mechanical-calendar__button--primary" to={`/maintenance/mech-management?building=${encodeURIComponent(selected.buildingObjectId)}`}>Open building overview</Link>}
        </Modal.Footer>
      </Modal>
    </main>
  );
}
